import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { IoIosEye } from "react-icons/io";
import { IoIosEyeOff } from "react-icons/io";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { signupSchema } from "@/utils/schemaHelper";

import { SignInSuccess } from "@/enums/errorMessages";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  logoutUser,
  signupUser,
  verifySignupTokenApi,
} from "@/modules/authenticationService";

import { TiTick } from "react-icons/ti";
import { logout } from "@/store/redux/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { Profile } from "@/types/authentication";
import { Loader2 } from "lucide-react";
import { ROUTES } from "@/enums/routes";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { ReusableButton } from "@/reusable/reusableButton";
import { setLoading } from "@/store/redux/appSlice";

const SignUpComponent = () => {
  const { token } = useParams();
  if (!token) {
    return <div>Invalid or missing signup link.</div>;
  }

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email");
  const loading = useSelector((state: RootState) => state.app.loading);
  const user = useSelector(
    (state: RootState) => state.auth.profile,
  ) as Profile | null;
  const dispatch = useDispatch();
  const [tokenVerified, setTokenVerified] = useState<boolean>(false);
  useEffect(() => {
    const verifyToken = async (token: string) => {
      try {
        await verifySignupTokenApi(token);
        setTokenVerified(true);
      } catch (error) {
        const actualError = ErrorHelper(error);
        navigate(ROUTES.LOGIN);
        const message = Array.isArray(actualError)
          ? actualError[0]
          : actualError;

        toastMessage("error", message);
      }
    };

    verifyToken(token);
  }, [token]);

  useEffect(() => {
    if (user) {
      logoutUser(user.refreshToken);
      dispatch(logout());
    }
  }, [user, dispatch]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      username: "",
      email: email ?? "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof signupSchema>) => {
    if (loading) return;

    dispatch(setLoading(true));
    try {
      await signupUser(values, token);
      navigate(ROUTES.ORGANIZATION_FORM);
      toastMessage("success", SignInSuccess.SIGN_IN_SUCCESS);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (tokenVerified === false) {
    // You can show a loading spinner here
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center w-screen bg-gray-100 px-4 ">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-center text-2xl font-semibold mb-6 text-gray-800">
          Sign Up
        </h2>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>First Name</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Last Name</FormLabel>

                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Username</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        {...field}
                        className={`${
                          field.value && !form.formState.errors.username
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.username && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                  </div>

                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Email</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        readOnly
                        {...field}
                        className={`${
                          field.value && !form.formState.errors.email
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.email && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Password</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.password
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.password && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                    <div
                      className="absolute right-7 top-2 cursor-pointer"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                    {}
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="max-h-13">
                  <FormLabel>Confirm Password</FormLabel>

                  <div className="relative">
                    <FormControl>
                      <Input
                        className={`${
                          field.value && !form.formState.errors.confirmPassword
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Enter Password"
                        {...field}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value &&
                        !form.formState.errors.confirmPassword && (
                          <TiTick className="text-green-700" />
                        )}
                    </div>
                    <div
                      className="absolute right-7 top-2 cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? <IoIosEye /> : <IoIosEyeOff />}
                    </div>
                  </div>
                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />
            <div className="flex justify-center ">
              <ReusableButton
                label="Sign Up"
                className="w-full"
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default SignUpComponent;
